import React, { useContext, useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import MessageDate from './message-date';
import MessageAvatar from './message-avatar';
import { MessageContext } from '../chat-messages';
import MessageContainer from './message-container';

const MessagesShow = () => {
    const theme = useTheme();
    const messageHook = useContext(MessageContext);
    const {
        userInfo,
        messages,
        avatarPrimaryRoute,
        avatarSecondaryRoute,
        onReply,
        gatewayOwnerId,
        gatewayName,
    } = messageHook!;

    const memoizedMessages = useMemo(() => {
        if (!messages) return null;

        return messages
            .slice(0)
            .reverse()
            .map((message: any, i: number) => {
                const isMe =
                    message?.user?.id !== undefined &&
                    userInfo?.id !== undefined &&
                    Boolean(message?.user?.isAdmin) === Boolean(userInfo?.isAdmin) &&
                    String(message?.user?.id) === String(userInfo?.id);
                const isUserSide = message?.created_by
                    ? message.created_by === 'user'
                    : !message?.user?.isAdmin;
                const id = message?.user?.id;
                const avatar = message?.user?.avatar;
                const name = message?.user?.name;
                const family = message?.user?.family;
                const isSupport = !!message?.user?.isAdmin && !isMe;
                const isGatewayOwner =
                    !message?.user?.isAdmin &&
                    gatewayOwnerId !== undefined &&
                    String(message?.user?.id) === String(gatewayOwnerId);
                const isGatewayMember =
                    !message?.user?.isAdmin &&
                    gatewayOwnerId !== undefined &&
                    String(message?.user?.id) !== String(gatewayOwnerId);
                const fullName = [name, family].filter(Boolean).join(' ');
                const gatewayRoleName = gatewayName ? `درگاه ${gatewayName}` : 'درگاه';
                const displayName = isMe
                    ? 'شما'
                    : isSupport
                      ? 'کارشناس پشتیبانی'
                      : isGatewayMember
                        ? `${fullName || 'کاربر'} (نماینده ${gatewayRoleName})`
                        : isGatewayOwner
                          ? `${fullName || 'کاربر'} (مالک ${gatewayRoleName})`
                          : fullName || 'کاربر';

                if (message?.status !== 'active' && !userInfo?.isAdmin) {
                    return null;
                }

                return (
                    <Box key={message?.id ?? i} sx={{ mb: 2.25, width: '100%' }}>
                        <MessageDate createdAt={message?.created_at ?? ''} index={i} />

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isUserSide ? 'row' : 'row-reverse',
                                width: '100%',
                                alignItems: 'flex-start',
                            }}>
                            <MessageAvatar
                                isMe={isUserSide}
                                avatar={avatar}
                                name={displayName}
                                family={undefined}
                                route={
                                    isMe
                                        ? avatarPrimaryRoute + id
                                        : avatarSecondaryRoute + id
                                }
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxWidth: { xs: '82%', sm: '70%', md: '64%' },
                                    minWidth: { xs: 0, sm: 180 },
                                    alignItems: isUserSide ? 'flex-start' : 'flex-end',
                                }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        mb: 0.5,
                                        px: 0.5,
                                    }}>
                                    {displayName}
                                </Typography>

                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        gap: 0.75,
                                        flexDirection: isUserSide ? 'row' : 'row-reverse',
                                    }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <MessageContainer
                                            message={message}
                                            isMe={isMe}
                                            isUserSide={isUserSide}
                                            index={i}
                                        />
                                    </Box>

                                    {!!onReply && message?.id > 0 && message?.status === 'active' && (
                                        <Tooltip title="پاسخ" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => onReply(message)}
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    flexShrink: 0,
                                                    mb: 0.25,
                                                    color: 'text.secondary',
                                                    bgcolor: alpha(
                                                        theme.palette.text.primary,
                                                        theme.palette.mode === 'dark' ? 0.06 : 0.035,
                                                    ),
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                        borderColor: alpha(theme.palette.primary.main, 0.3),
                                                        bgcolor: alpha(
                                                            theme.palette.primary.main,
                                                            theme.palette.mode === 'dark' ? 0.12 : 0.07,
                                                        ),
                                                    },
                                                }}>
                                                <i
                                                    className="fa-light fa-reply"
                                                    style={{ fontSize: 13 }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            });
    }, [
        messages,
        userInfo,
        avatarPrimaryRoute,
        avatarSecondaryRoute,
        onReply,
        gatewayOwnerId,
        gatewayName,
        theme,
    ]);

    return <>{memoizedMessages}</>;
};

export default MessagesShow;
